import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CubeGrid } from '../components/ui/CountingCubes';
import { getLessonById, getNextLesson, ENCOURAGEMENTS } from '../data/curriculum';
import { Star, RotateCcw, ChevronRight, Home, Trophy, Target } from 'lucide-react';

const LessonCompletePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accuracy, correctAnswers, totalExercises, lessonId } = location.state || {};

  const lesson = getLessonById(lessonId);
  const nextLesson = getNextLesson(lessonId);
  
  // Determine result status
  const isExcellent = accuracy >= 85;
  const isGood = accuracy >= 60 && accuracy < 85;
  const needsRetry = accuracy < 60;

  const getMessage = () => {
    if (isExcellent) return 'Отлично!';
    if (isGood) return 'Хорошо!';
    return 'Попробуем ещё раз!';
  };

  const getSubMessage = () => {
    if (isExcellent) return 'Ты отлично справился!';
    if (isGood) return 'Можешь попробовать ещё раз или двигаться дальше';
    return 'Давай повторим этот урок';
  };

  const getStars = () => {
    if (accuracy >= 85) return 3;
    if (accuracy >= 60) return 2;
    return 1;
  };

  const stars = getStars();

  return (
    <div className="app-container min-h-screen bg-gradient-soft flex flex-col px-6 py-8 safe-area-top safe-area-bottom">
      {/* Decorative cubes */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ delay: 0.5 }}
        className="absolute top-10 left-6"
      >
        <CubeGrid count={2} size="sm" animate={false} />
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ delay: 0.7 }}
        className="absolute top-16 right-8"
      >
        <CubeGrid count={3} size="sm" animate={false} />
      </motion.div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center">
        {/* Stars */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="flex gap-2 mb-6"
        >
          {[1, 2, 3].map((i) => (
            <motion.div
              key={i}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ 
                scale: i <= stars ? 1 : 0.7, 
                rotate: 0,
                opacity: i <= stars ? 1 : 0.3
              }}
              transition={{ delay: 0.3 + i * 0.15, type: 'spring', stiffness: 200 }}
            >
              <Star 
                className={`w-12 h-12 ${
                  i <= stars ? 'text-warning fill-warning' : 'text-muted'
                }`} 
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Trophy/Target icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
          className={`w-24 h-24 rounded-3xl flex items-center justify-center mb-6 ${
            isExcellent 
              ? 'bg-correct-light' 
              : isGood 
              ? 'bg-warning/20'
              : 'bg-secondary-light'
          }`}
        >
          {isExcellent ? (
            <Trophy className="w-12 h-12 text-success" />
          ) : (
            <Target className={`w-12 h-12 ${isGood ? 'text-warning' : 'text-secondary'}`} />
          )}
        </motion.div>

        {/* Message */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-3xl font-display font-bold text-foreground mb-2 text-center"
        >
          {getMessage()}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-muted-foreground text-center mb-8"
        >
          {getSubMessage()}
        </motion.p>

        {/* Score display */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
          className="bg-card rounded-2xl p-6 shadow-card mb-8 text-center min-w-[200px]"
        >
          <p className="text-5xl font-display font-bold text-foreground mb-1">
            {accuracy}%
          </p>
          <p className="text-sm text-muted-foreground">
            {correctAnswers} из {totalExercises} правильно
          </p>
        </motion.div>

        {/* Lesson title */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="text-sm text-muted-foreground mb-8"
        >
          Урок {lessonId}: {lesson?.title}
        </motion.p>
      </div>

      {/* Action buttons */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="space-y-3"
      >
        {/* Primary action */}
        {isExcellent && nextLesson ? (
          <Button
            onClick={() => navigate(`/lesson/${nextLesson.id}`)}
            className="btn-child-primary w-full flex items-center justify-center gap-2"
          >
            Следующий урок
            <ChevronRight className="w-5 h-5" />
          </Button>
        ) : (
          <Button
            onClick={() => navigate(`/lesson/${lessonId}`)}
            className="btn-child-primary w-full flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" />
            Попробовать ещё раз
          </Button>
        )}

        {/* Secondary actions */}
        {isExcellent && (
          <Button
            onClick={() => navigate(`/lesson/${lessonId}`)}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 h-14 rounded-xl"
          >
            <RotateCcw className="w-5 h-5" />
            Повторить урок
          </Button>
        )}

        {isGood && nextLesson && (
          <Button
            onClick={() => navigate(`/lesson/${nextLesson.id}`)}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 h-14 rounded-xl"
          >
            Продолжить
            <ChevronRight className="w-5 h-5" />
          </Button>
        )}

        <Button
          onClick={() => navigate('/home')}
          variant="ghost"
          className="w-full flex items-center justify-center gap-2"
        >
          <Home className="w-5 h-5" />
          Главная
        </Button>
      </motion.div>
    </div>
  );
};

export default LessonCompletePage;
